// @flow
import * as React from "react";
import styled, { css } from "styled-components";
import Media from "react-media";
import idx from "idx";
import Confetti from "react-confetti";
import Measure from "../../../common/Measure";
import Gettext from "../../../common/Gettext";
import SimpleSpinner from "../../../Spinners/SimpleSpinner";
import _Link from "../../Link";

import NextTournament from "./NextTournament";
import Leaderboard from "./Leaderboard";
import TournamentApi from "../../Api";

import ShareTournament from "../../../Tournament/ShareTournament";

import { FinalCup, WinnerCup } from "../../../Icons/Tournaments";
import * as S from "./styles";

import type { Tournament as TournamentProps } from "../../../../redux/CommonTypes";

const Popup = styled(S.Popup)`
  background-image: none;

  @media (min-width: 960px) {
    padding: 25px 0 0;
  }

  @media (max-width: 959px) {
    padding: 16px 0 0;
  }
`;

const Wrapper = styled(S.Wrapper)`
  direction: ${({ theme }) => theme.direction};
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  align-content: space-between;
  justify-content: space-between;
  flex: 1;
  height: 100%;
`;

const ContentView = styled.div`
  margin: auto 0;
  width: 100%;
  line-height: 1.25;
`;

const LeaderboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 86px;
  padding: 0 10px;
  box-sizing: border-box;
  overflow: hidden;

  @media (min-width: 960px) {
    margin-top: 15px;
    padding: 0 15px;
    min-height: 122px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-content: center;
  justify-content: center;
  align-items: center;

  @media (max-width: 959px) {
    flex-flow: row wrap;
    padding: 10px 10px 0;
  }
`;

const HeaderContents = styled.div`
  @media (max-width: 959px) {
    padding: 0 10px;
  }

  ${({ winner }) => {
    if (winner === false) {
      return css`
        @media (max-width: 479px) {
          flex-grow: 0;
          flex-shrink: 1;
          flex-basis: 70%;
        }
      `;
    }
  }};
`;

const Heading = styled.div`
  margin: 15px auto;
  font-weight: 700;
  font-size: 18px;
  color: ${({ theme }) => theme.accent};

  @media (max-width: 959px) {
    margin: 10px 0;
    text-align: center;
  }

  @media (max-width: 479px) {
    font-size: 14px;
  }
`;

const Info = styled.div`
  margin: 15px auto;
  font-weight: 700;
  font-size: 32px;
  color: ${({ theme }) => theme.foreground};

  @media (max-width: 959px) {
    margin: 10px 0;
    font-size: 18px;
    text-align: center;
  }
`;

const Link = styled(_Link)`
  @media (max-width: 959px) {
    margin-top: 10px;
  }
`;

type ContentProps = {
  ...TournamentProps,
  winner?: boolean,
  icon: React.Node,
  children: React.Node,
  leaderboard: React.Node
};

function Content({ winner = false, ...props }: ContentProps) {
  return (
    <ContentView>
      <Header>
        <div>{props.icon}</div>
        <HeaderContents winner={winner}>
          <Heading>
            <Gettext>The tournament {props.title} is finished</Gettext>
          </Heading>
          <Info>{props.children}</Info>
        </HeaderContents>
      </Header>
      <LeaderboardContainer>{props.leaderboard}</LeaderboardContainer>
      <Link to={`/tournaments/${props.id}`}>
        <Gettext>All results</Gettext>
      </Link>
    </ContentView>
  );
}

type SharedProps = {|
  id: number,
  place?: number,
  prize: string,
  prize_usd: string,
  onDismiss: () => void,
  style?: string
|};

type Props = {
  ...SharedProps,
  tournament: ?TournamentProps,
  api: any
};

function Final({ style, onDismiss, api, tournament, ...props }: Props) {
  if (tournament == null) {
    return (
      <Popup onDismiss={onDismiss} style={style} fades={false}>
        <Wrapper>
          <SimpleSpinner />
        </Wrapper>
      </Popup>
    );
  }

  const place = props.place || tournament.current_place;
  let winner = false;

  if (props.prize != null) {
    winner = true;
  } else {
    winner =
      tournament.current_place != null &&
      tournament.prizes.length >= tournament.current_place &&
      tournament.prizes[tournament.current_place - 1].user_id != null;
  }

  return (
    <Popup onDismiss={onDismiss} style={style} fades={false}>
      <Wrapper>
        <Media query="(max-width: 959px)">
          {matches => {
            if (winner === true) {
              return (
                <Content
                  {...tournament}
                  winner
                  icon={<WinnerCup width={matches ? "300px" : "464px"} />}
                  leaderboard={
                    <Leaderboard {...tournament} api={api} limit={1} winner />
                  }
                >
                  <Gettext>YOU WIN</Gettext>
                </Content>
              );
            }

            return (
              <Content
                {...tournament}
                icon={<FinalCup width={matches ? "61px" : "123px"} />}
                leaderboard={
                  <Leaderboard {...tournament} api={api} limit={3} />
                }
              >
                <Gettext>Your results</Gettext>:
              </Content>
            );
          }}
        </Media>
        <ShareTournament
          title={tournament.title}
          currentPlace={tournament.current_place}
          prize={tournament.current_place &&
            tournament.prizes[tournament.current_place - 1] &&
            tournament.prizes[tournament.current_place - 1].prize}
          tournamentId={tournament.id}
          prizePlacesCount={tournament.prizes.length}
          isPopup
        />
        <NextTournament onDismiss={onDismiss} />
      </Wrapper>
      {winner && place === 1 && (
        <Measure>
          {({ width, height, innerRef, didMeasure }) => (
            <div
              ref={innerRef}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none"
              }}
            >
              {didMeasure && (
                <Confetti
                  width={width}
                  height={height}
                  numberOfPieces={100}
                  recycle={false}
                  gravity={0.5}
                />
              )}
            </div>
          )}
        </Measure>
      )}
    </Popup>
  );
}

export default (props: SharedProps) => (
  <TournamentApi.Consumer>
    {api => {
      const tournament = idx(
        api.readEntity(`/api/tournaments/info?id=${props.id}`),
        tournament => tournament.value
      );

      return (
        <Final key={props.id} api={api} tournament={tournament} {...props} />
      );
    }}
  </TournamentApi.Consumer>
);



// WEBPACK FOOTER //
// ./src/components/Tournaments/Popups/Final/NewFinal.js